import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Polyhedron } from "@react-three/drei";
import { forwardRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface PolyhedronMeshProperties extends TransformableItemProperties {
  radius: number;
  detail: number;
  materialId?: string;
}

export interface PolyhedronMesh extends Mesh<PolyhedronMeshProperties> {
  subtype: 'polyhedron';
  properties: PolyhedronMeshProperties;
}

// Create a reusable Polyhedron component that can be used in both preview and world
const PolyhedronComponent = forwardRef<THREE.Mesh, { properties: PolyhedronMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { radius, detail, transform, materialId } = properties;
  const { activeWorld } = useAppContext();
  
  // Define vertices and indices for a simple polyhedron (tetrahedron)
  const vertices = [
    1, 1, 1,
    -1, -1, 1,
    -1, 1, -1,
    1, -1, -1
  ];
  
  const indices = [
    0, 1, 2,
    0, 2, 3,
    0, 3, 1,
    1, 3, 2
  ];
  
  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshStandardMaterial({ color: "#808080" });
  }, [material, material?.properties, isPreview]);

  return (
    <Polyhedron
      ref={ref}
      args={[vertices, indices, radius, detail]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const PolyhedronMeshItem: PolyhedronMesh = {
  id: '',
  name: 'Polyhedron',
  type: 'mesh',
  subtype: 'polyhedron',
  icon: 'Polyhedron',
  properties: {
    radius: 1,
    detail: 0,
    materialId: '',
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.radius',
      type: 'number',
      label: 'Radius',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.detail',
      type: 'number',
      label: 'Detail',
      default: 0,
      panel: 'geometry'
    }
  ],
  render: (properties: PolyhedronMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <PolyhedronComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
}; 